import { patchViewCacheByPrefix } from '@/lib/viewCache';
import type { DashboardGroupData } from './types';

/** Prefix shared by every cached view (filters, search, view type...) of a dashboard. */
export function getDashboardCachePrefix(dashboardId: number | string): string
{
  return `C2_Cache_Dashboard_${dashboardId}_`;
}

/**
 * Removes cards that left a dashboard from all of its cached views, so returning
 * to it does not paint them from the cache before the API responds.
 */
export function removeConversationsFromDashboardCache(
  dashboardId: number | null | undefined,
  conversationIds: number[],
): void
{
  if (!dashboardId || conversationIds.length === 0) return;
  const ids = new Set(conversationIds.map(Number));

  patchViewCacheByPrefix<DashboardGroupData[]>(getDashboardCachePrefix(dashboardId), groups =>
  {
    if (!Array.isArray(groups)) return null;
    return groups.map(group =>
    {
      const conversations = group.Conversations || [];
      const kept = conversations.filter(c => !ids.has(c.ConversationId));
      if (kept.length === conversations.length) return group;
      const removed = conversations.length - kept.length;
      return {
        ...group,
        Conversations: kept,
        TotalItems: Math.max(0, (group.TotalItems ?? conversations.length) - removed),
      };
    });
  });
}

/**
 * Call after cards were moved to another dashboard: removes them from the source
 * columns still on screen and from the source dashboard's cached views.
 * groupId 0/undefined (e.g. moved from the InfoWindow) only patches the cache.
 */
export function removeMovedCardsFromSource(
  sourceDashboardId: number | null | undefined,
  moved: Array<{ conversationId: number; groupId?: number | null }>,
): void
{
  moved.forEach(({ conversationId, groupId }) =>
  {
    if (!groupId) return;
    window.dispatchEvent(
      new CustomEvent('corgee:card-removed-from-group', { detail: { conversationId, groupId } }),
    );
  });
  removeConversationsFromDashboardCache(
    sourceDashboardId,
    moved.map(m => m.conversationId),
  );
}
